/**
 * Fact Checker service API calls — thin wrappers around the HTTP client.
 *
 * All POST calls are fire-and-forget (202); callers must poll with the
 * returned job_id until status === 'completed' | 'failed'.
 */

import { factCheckerApi } from './client';
import type {
  ApiResponse,
  ExtractJobOut,
  ValidateJobOut,
} from '../types';

// ---------------------------------------------------------------------------
// Extraction
// ---------------------------------------------------------------------------

export interface StartExtractionParams {
  text: string;
  context?: string;
  options?: {
    min_confidence?: number;
    max_claims_per_sentence?: number;
    include_descriptive_claims?: boolean;
  };
}

export async function startExtraction(
  params: StartExtractionParams,
): Promise<ApiResponse<{ job_id: string; status: string; created_at: string }>> {
  return factCheckerApi.post('/v1/extract', params);
}

export async function getExtractionJob(
  jobId: string,
): Promise<ApiResponse<ExtractJobOut>> {
  return factCheckerApi.get(`/v1/extract/${jobId}`);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface ClaimRef {
  claim_id?: string;
  text?: string;
  context?: string | null;
}

export interface WebSearchSource {
  type: 'web_search';
  max_results?: number;
  search_query_override?: string | null;
}

export interface PaperIndexSource {
  type: 'paper_index';
  index_id: string;
  top_k?: number;
}

/** Default number of literature-index chunks retrieved per claim. */
export const PAPER_INDEX_TOP_K = 12;

export type ValidationSource = WebSearchSource | PaperIndexSource;

export async function startValidation(params: {
  claims: ClaimRef[];
  sources?: ValidationSource[];
}): Promise<
  ApiResponse<{ job_id: string; status: string; total_claims: number; created_at: string }>
> {
  return factCheckerApi.post('/v1/validate', {
    claims: params.claims,
    sources: params.sources ?? [{ type: 'web_search' }],
  });
}

export async function getValidationJob(
  jobId: string,
): Promise<ApiResponse<ValidateJobOut>> {
  return factCheckerApi.get(`/v1/validate/${jobId}`);
}

// ---------------------------------------------------------------------------
// Polling helper
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2_000;

/** Poll a job endpoint until status is 'completed', 'failed', or 'cancelled'. No client timeout. */
export async function pollJob<T extends { status: string; error?: string | null }>(
  fetchFn: () => Promise<ApiResponse<T>>,
  onPoll?: (data: T) => void,
): Promise<T> {
  for (;;) {
    const response = await fetchFn();

    if (!response.data) {
      throw new Error(response.error?.message ?? 'Empty response from server');
    }

    const data = response.data;
    onPoll?.(data);

    if (data.status === 'completed') return data;
    if (data.status === 'failed') {
      throw new Error(data.error ?? 'Job failed without error detail');
    }
    if (data.status === 'cancelled') {
      throw new Error('Job was cancelled');
    }

    await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}
